import React, { Fragment } from 'react';

import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { mdiLink, mdiFolderOutline } from '@lumapps/lumx/icons';
import { Size, Theme } from '@lumapps/lumx/react';
import { isLinkNode, isSectionNode, NavigationMapItem } from '@lumapps/navigation/types';
import { GLOBAL, TranslatableObject, TranslateObject, useTranslate } from '@lumapps/translations';

/** Refer to the types in `NavigationItemDetails` */
export interface NoPageNavigationItemDetailsProps {
    currentLanguage: string;
    displayVisibility: boolean;
    isDisabled?: boolean;
    node: NavigationMapItem;
    nodeVisibilityFeeds?: { id: string; name: string }[];
    theme?: Theme;
    titleToUse?: TranslatableObject;
    translatedTitle?: TranslateObject;
}

/**
 * Navigation Item Details for:
 * - Link nodes
 * - Section nodes
 * - Generic nodes (fallback for nodes which type is not handled yet)
 */
export const NoPageNavigationItemDetails: React.FC<NoPageNavigationItemDetailsProps> = ({
    node,
    nodeVisibilityFeeds,
    titleToUse,
    translatedTitle,
    currentLanguage,
    isDisabled,
    displayVisibility,
    theme,
}) => {
    const { translateKey, translateObject } = useTranslate();
    const title = titleToUse?.value || translateObject(translatedTitle, currentLanguage, false);

    /** Display the visibility of the node in the additional info, if displayVisibility === true */
    const nodeVisibilityAdditionalInfo = [];

    if (displayVisibility && nodeVisibilityFeeds) {
        nodeVisibilityAdditionalInfo.push(
            <Fragment key="feeds">
                {translateKey(GLOBAL.VISIBILITY)}:&nbsp;
                {nodeVisibilityFeeds.map((feed) => translateKey(feed.name)).join(', ')}
            </Fragment>,
        );
    }
    let icon;
    let url;

    if (isLinkNode(node)) {
        icon = mdiLink;
        url = node.url;

        // Show title and URL OR only the URL (as a title).
        if (title) {
            nodeVisibilityAdditionalInfo.push(<Fragment key="url">{url}</Fragment>);
        }
    }
    if (isSectionNode(node)) {
        icon = mdiFolderOutline;
    }

    return (
        <GenericEntityBlock
            isDisabled={isDisabled}
            metadata={nodeVisibilityAdditionalInfo.length > 0 ? nodeVisibilityAdditionalInfo : undefined}
            theme={theme}
            thumbnail={{ icon, size: Size.m }}
            title={title}
        />
    );
};
